import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";

/**
 * ProjectTypeDropdown Component
 *
 * Displays a dropdown menu with different project types for creation.
 * Some options are enabled (navigate to forms) while others are disabled.
 */
const ProjectTypeDropdown = ({
  buttonClassName = "",
  dropdownClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Project type options
  const projectTypes = [
    {
      id: "ground-up",
      label: "Ground-Up Development",
      description: "New construction projects from the ground up",
      enabled: true,
      template: "basic",
    },
    {
      id: "buy-to-let",
      label: "Buy-To-Let",
      description: "Investment properties for rental income",
      enabled: false,
      template: null,
    },
    {
      id: "heavy-refurb",
      label: "Heavy Refurb",
      description: "Major renovation and refurbishment projects",
      enabled: false,
      template: null,
    },
    {
      id: "light-refurb",
      label: "Light Refurb",
      description: "Minor renovation and improvement projects",
      enabled: false,
      template: null,
    },
    {
      id: "startup-scaleup",
      label: "Startup Scale-Up",
      description: "PropTech startup seeking investment and growth",
      enabled: true,
      template: "startup",
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown when an option is selected
  const handleOptionSelect = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${buttonClassName}`}
      >
        <PlusIcon className="h-5 w-5" />
        <span>New Project</span>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${dropdownClassName}`}
        >
          <div className="py-2">
            <div className="px-4 py-2 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-900">
                Select Project Type
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Choose the type of project you want to create
              </p>
            </div>

            {projectTypes.map((type) => (
              <div key={type.id} className="px-2">
                {type.enabled ? (
                  <Link
                    to={`/new-project?template=${type.template}`}
                    onClick={handleOptionSelect}
                    className="flex flex-col px-3 py-3 text-left hover:bg-gray-50 rounded-md transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                        {type.label}
                      </span>
                      <span className="text-xs text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Available
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      {type.description}
                    </span>
                  </Link>
                ) : (
                  <div className="flex flex-col px-3 py-3 text-left opacity-50 cursor-not-allowed">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-400">
                        {type.label}
                      </span>
                      <span className="text-xs text-gray-400 font-medium">
                        Coming Soon
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 mt-1">
                      {type.description}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTypeDropdown;
